import { ApiResponseError } from '../errors/ApiError.js';

// Maps the POST /v1/adbreaks/resolve response onto the EXISTING AdBreak
// representation (brief §5: "Do not create a second incompatible AdBreak
// model if the existing model can be reused").
//
// It deliberately does NOT construct `AdBreak` instances. The widget already
// builds those itself from the plain `{ id, type, position, vastUrl }` objects
// that `AudioWidget.load({ adBreaks })` accepts, and the documented response is
// already exactly that shape. Returning plain descriptors means:
//   - no second model and no duplicated validation,
//   - the API layer stays free of a `widgets/` import, preserving the
//     dependency direction,
//   - a resolved schedule and a hand-written one are indistinguishable to the
//     widget, so nothing downstream can behave differently.
export function mapAdBreaks(data) {
  const adBreaks = data?.adBreaks;

  if (!Array.isArray(adBreaks)) {
    throw new ApiResponseError('Ad break response did not contain an adBreaks array', {
      endpoint: '/v1/adbreaks/resolve',
      context: { endpoint: '/v1/adbreaks/resolve' },
    });
  }

  // The backend sends each break's VAST waterfall as `vastUrls`, highest CPM
  // first. The widget plays one tag per break, so the first one is used. A
  // break with no demand is dropped here rather than handed to the widget as a
  // break that can only fail (the backend's own contract: skip it, not error).
  return Object.freeze(
    adBreaks
      .map((entry) => ({ entry, vastUrl: firstTag(entry) }))
      .filter(({ vastUrl }) => vastUrl !== null)
      .map(({ entry, vastUrl }) =>
        Object.freeze({
          id: entry?.id ?? null,
          type: entry?.type ?? null,
          position: typeof entry?.position === 'number' ? entry.position : 0,
          vastUrl,
        }),
      ),
  );
}

function firstTag(entry) {
  if (Array.isArray(entry?.vastUrls)) {
    return entry.vastUrls.find((u) => typeof u === 'string' && u.length > 0) ?? null;
  }
  return typeof entry?.vastUrl === 'string' && entry.vastUrl ? entry.vastUrl : null;
}
